import { apiClient } from './api';

interface SignedUrlRequest {
  s3Key: string;
  type: 'thumbnail' | 'full';
  expiresIn?: number;
}

interface SignedUrlResponse {
  signedUrl: string;
  expiresAt: string;
}

interface CachedSignedUrl {
  url: string;
  expiresAt: Date;
}

class SignedUrlService {
  private cache = new Map<string, CachedSignedUrl>();
  private pendingRequests = new Map<string, Promise<string>>();

  /**
   * Get a signed URL for an image, with caching to avoid duplicate requests
   */
  async getSignedUrl(s3Key: string, type: 'thumbnail' | 'full', expiresIn = 3600): Promise<string> {
    const cacheKey = `${s3Key}-${type}`;
    
    // Check if we have a valid cached URL
    const cached = this.cache.get(cacheKey);
    if (cached && cached.expiresAt > new Date(Date.now() + 300000)) { // 5 min buffer
      return cached.url;
    }

    // Check if there's already a pending request for this URL
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return pending;
    }

    // Create new request
    const requestPromise = this.fetchSignedUrl(s3Key, type, expiresIn);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const url = await requestPromise;
      this.pendingRequests.delete(cacheKey);
      return url;
    } catch (error) {
      this.pendingRequests.delete(cacheKey);
      throw error;
    }
  }

  private async fetchSignedUrl(s3Key: string, type: 'thumbnail' | 'full', expiresIn: number): Promise<string> {
    try {
      const request: SignedUrlRequest = {
        s3Key,
        type,
        expiresIn
      };

      const response = await apiClient.post<{ data: SignedUrlResponse }>('/signed-url', request);
      
      if (!response.data?.data?.signedUrl) {
        throw new Error('Invalid response from signed URL service');
      }

      const { signedUrl, expiresAt } = response.data.data;
      
      // Cache the URL
      const cacheKey = `${s3Key}-${type}`;
      this.cache.set(cacheKey, {
        url: signedUrl,
        expiresAt: new Date(expiresAt)
      });

      return signedUrl;
    } catch (error) {
      console.error('Failed to get signed URL:', error);
      
      // Fallback to direct CloudFront URL (less secure but functional)
      const cloudFrontDomain = process.env.REACT_APP_PHOTOS_DOMAIN || 'photos.stielstras.com';
      
      if (type === 'thumbnail') {
        const lastDotIndex = s3Key.lastIndexOf('.');
        const nameWithoutExtension = lastDotIndex > 0 ? s3Key.substring(0, lastDotIndex) : s3Key;
        const extension = lastDotIndex > 0 ? s3Key.substring(lastDotIndex) : '.jpg';
        const thumbnailKey = `${nameWithoutExtension}-thumb-400${extension}`;
        return `https://${cloudFrontDomain}/thumbnails/${thumbnailKey}`;
      } else {
        return `https://${cloudFrontDomain}/${s3Key}`;
      }
    }
  }

  /**
   * Preload signed URLs for a batch of images to improve performance
   */
  async preloadSignedUrls(photos: Array<{ s3Key: string }>, type: 'thumbnail' | 'full'): Promise<void> {
    const promises = photos.map(photo => 
      this.getSignedUrl(photo.s3Key, type).catch(error => {
        console.warn(`Failed to preload signed URL for ${photo.s3Key}:`, error);
      })
    );
    
    await Promise.allSettled(promises);
  }

  /**
   * Clear expired URLs from cache
   */
  clearExpiredUrls(): void {
    const now = new Date();
    for (const [key, cached] of this.cache.entries()) {
      if (cached.expiresAt <= now) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cached URLs
   */
  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

export const signedUrlService = new SignedUrlService();

// Clean up expired URLs every 5 minutes
setInterval(() => {
  signedUrlService.clearExpiredUrls();
}, 5 * 60 * 1000);